import { useMemo } from 'react';

import { useCompanyQuery, UserRole } from 'api/graphql';
import { useAuth } from 'hooks/useAuth';
import { canManageResourceCategories, typeGuards } from 'utils';

/**
 * Resolves whether the current user may manage resource categories (teams).
 * Loads company.customerType for company admins from the company query.
 */
export const useCanManageResourceCategories = (): {
  canManage: boolean;
  loading: boolean;
  companyId?: string;
} => {
  const { role, user } = useAuth();

  const companyId = useMemo(() => {
    if (role !== UserRole.CompanyAdmin) {
      return undefined;
    }
    const companyProfile = user?.userProfile
      ?.map((profile) => typeGuards.companyProfile(profile))
      .find(Boolean);
    const id = companyProfile?.company?.id;
    return id != null ? String(id) : undefined;
  }, [role, user]);

  const { data, loading } = useCompanyQuery({
    variables: { id: companyId || '' },
    skip: role !== UserRole.CompanyAdmin || !companyId,
    fetchPolicy: 'cache-first',
  });

  const canManage = canManageResourceCategories(
    role,
    data?.company?.customerType
  );

  return { canManage, loading, companyId };
};
